/* eslint-disable max-statements */
/* eslint-disable max-params */
/* eslint-disable max-depth */
/* eslint-disable max-lines */
import { nearestSixteenth, Vertex2 } from '@rta/rta-blueprint-library';

import { Vector } from '@r-core/types';

/**
 * Calculates the bounding box of a set of vectors
 */
const getBoundingBox = (vectors: Array<Vector<Vertex2>>): { minX: number, maxX: number, minY: number, maxY: number } => {
	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;

	for (const vector of vectors) {
		const fromX = nearestSixteenth(vector.from.x);
		const toX = nearestSixteenth(vector.to.x);
		const fromY = nearestSixteenth(vector.from.y);
		const toY = nearestSixteenth(vector.to.y);

		minX = Math.min(minX, fromX, toX);
		maxX = Math.max(maxX, fromX, toX);
		minY = Math.min(minY, fromY, toY);
		maxY = Math.max(maxY, fromY, toY);
	}

	return { minX, maxX, minY, maxY };
};

/**
 * Clips a vector to a bounding box while preserving direction.
 * - Horizontal vectors outside Y bounds are MOVED to the nearest Y boundary
 * - Vertical vectors outside X bounds are MOVED to the nearest X boundary
 * - X/Y ranges are clipped to bounds
 */
const clipVectorToBounds = (
	vector: Vector<Vertex2>,
	bounds: { minX: number, maxX: number, minY: number, maxY: number }
): Vector<Vertex2> | null => {
	const fromX = nearestSixteenth(vector.from.x);
	const toX = nearestSixteenth(vector.to.x);
	const fromY = nearestSixteenth(vector.from.y);
	const toY = nearestSixteenth(vector.to.y);

	// Horizontal vector
	if (fromY === toY) {
		// Move Y to boundary if outside
		let clippedY = fromY;
		if (fromY < bounds.minY) clippedY = bounds.minY;
		if (fromY > bounds.maxY) clippedY = bounds.maxY;

		const vMinX = Math.min(fromX, toX);
		const vMaxX = Math.max(fromX, toX);

		// Check if X range intersects bounds
		if (vMaxX <= bounds.minX || vMinX >= bounds.maxX) return null;

		const clippedMinX = Math.max(vMinX, bounds.minX);
		const clippedMaxX = Math.min(vMaxX, bounds.maxX);

		if (clippedMinX >= clippedMaxX) return null;

		// Preserve direction: if original went right (fromX < toX), keep it
		const goesRight = fromX <= toX;
		return {
			...vector,
			from: { x: goesRight ? clippedMinX : clippedMaxX, y: clippedY } as Vertex2,
			to: { x: goesRight ? clippedMaxX : clippedMinX, y: clippedY } as Vertex2
		};
	}

	// Vertical vector
	if (fromX === toX) {
		// Move X to boundary if outside
		let clippedX = fromX;
		if (fromX < bounds.minX) clippedX = bounds.minX;
		if (fromX > bounds.maxX) clippedX = bounds.maxX;

		const vMinY = Math.min(fromY, toY);
		const vMaxY = Math.max(fromY, toY);

		// Check if Y range intersects bounds
		if (vMaxY <= bounds.minY || vMinY >= bounds.maxY) return null;

		const clippedMinY = Math.max(vMinY, bounds.minY);
		const clippedMaxY = Math.min(vMaxY, bounds.maxY);

		if (clippedMinY >= clippedMaxY) return null;

		// Preserve direction: if original went up (fromY < toY), keep it
		const goesUp = fromY <= toY;
		return {
			...vector,
			from: { x: clippedX, y: goesUp ? clippedMinY : clippedMaxY } as Vertex2,
			to: { x: clippedX, y: goesUp ? clippedMaxY : clippedMinY } as Vertex2
		};
	}

	// Diagonal vectors - simplified check
	return vector;
};

/**
 * Filters out zero-length vectors
 */
const filterZeroLengthVectors = (vectors: Array<Vector<Vertex2>>): Array<Vector<Vertex2>> => {
	return vectors.filter((vector) => {
		const fromX = nearestSixteenth(vector.from.x);
		const toX = nearestSixteenth(vector.to.x);
		const fromY = nearestSixteenth(vector.from.y);
		const toY = nearestSixteenth(vector.to.y);
		return fromX !== toX || fromY !== toY;
	});
};

type Polygon = Array<Vector<Vertex2>>;
// type Vertex = { x: number, y: number };
type Bounds = { minX: number, maxX: number, minY: number, maxY: number };

/**
 * Converts a polygon (array of vectors) to an array of vertices
 */
const polygonToVertices = (polygon: Polygon): Array<Vertex2> => {
	return polygon.map((v) => (new Vertex2({ dataIn: { x: nearestSixteenth(v.from.x), y: nearestSixteenth(v.from.y) }})));
};

/**
 * Gets the bounding box of a polygon
 */
const getPolygonBounds = (polygon: Polygon): Bounds => {
	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;

	for (const vector of polygon) {
		const fromX = nearestSixteenth(vector.from.x);
		const toX = nearestSixteenth(vector.to.x);
		const fromY = nearestSixteenth(vector.from.y);
		const toY = nearestSixteenth(vector.to.y);

		minX = Math.min(minX, fromX, toX);
		maxX = Math.max(maxX, fromX, toX);
		minY = Math.min(minY, fromY, toY);
		maxY = Math.max(maxY, fromY, toY);
	}

	return { minX, maxX, minY, maxY };
};

/**
 * Checks if two bounding boxes overlap
 */
const boundsOverlap = (a: Bounds, b: Bounds): boolean => {
	return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
};

/**
 * Ray casting algorithm to determine if a point is inside a polygon.
 * Casts a ray from the point to the right and counts intersections with polygon edges.
 * Odd count = inside, even count = outside.
 */
const isPointInPolygon = (point: Vertex2, vertices: Array<Vertex2>): boolean => {
	const px = point.x;
	const py = point.y;
	let inside = false;

	for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
		const xi = vertices[i].x;
		const yi = vertices[i].y;
		const xj = vertices[j].x;
		const yj = vertices[j].y;

		// Check if point is exactly on vertex
		if ((xi === px && yi === py) || (xj === px && yj === py)) {
			return true; // On boundary counts as inside for our purposes
		}

		// Check if ray from point crosses this edge
		const intersect = ((yi > py) !== (yj > py)) && (px < (((xj - xi) * (py - yi)) / (yj - yi)) + xi);

		if (intersect) {
			inside = !inside;
		}
	}

	return inside;
};


/**
 * Finds which mask polygons overlap with which shape polygons.
 * Returns a map: shapeIndex -> array of maskIndices that overlap
 */
const findOverlappingPolygons = (
	shapePolygons: Array<Polygon>,
	maskPolygons: Array<Polygon>
): Map<number, Array<number>> => {
	const overlaps = new Map<number, Array<number>>();

	for (let si = 0; si < shapePolygons.length; si++) {
		const shapeBounds = getPolygonBounds(shapePolygons[si]);
		const shapeVertices = polygonToVertices(shapePolygons[si]);
		const overlappingMasks: Array<number> = [];

		for (let mi = 0; mi < maskPolygons.length; mi++) {
			const maskBounds = getPolygonBounds(maskPolygons[mi]);

			// Quick rejection: bounding boxes don't overlap
			if (!boundsOverlap(shapeBounds, maskBounds)) {
				continue;
			}

			// More detailed check: does any mask vertex fall inside the shape?
			const maskVertices = polygonToVertices(maskPolygons[mi]);
			let hasOverlap = false;

			for (const vertex of maskVertices) {
				if (isPointInPolygon(vertex, shapeVertices)) {
					hasOverlap = true;
					break;
				}
			}

			// Also check if any shape vertex falls inside the mask
			if (!hasOverlap) {
				for (const vertex of shapeVertices) {
					if (isPointInPolygon(vertex, maskVertices)) {
						hasOverlap = true;
						break;
					}
				}
			}

			if (hasOverlap) {
				overlappingMasks.push(mi);
			}
		}

		overlaps.set(si, overlappingMasks);
	}

	return overlaps;
};

/**
 * Checks if an edge (from shape) is collinear with and overlaps a mask edge
 */
const edgesOverlap = (
	shapeFrom: Vertex2,
	shapeTo: Vertex2,
	maskFrom: Vertex2,
	maskTo: Vertex2
): { overlaps: boolean, maskMin: number, maskMax: number, axis: 'x' | 'y' | null } => {
	const sfx = shapeFrom.x;
	const sfy = shapeFrom.y;
	const stx = shapeTo.x;
	const sty = shapeTo.y;
	const mfx = maskFrom.x;
	const mfy = maskFrom.y;
	const mtx = maskTo.x;
	const mty = maskTo.y;

	// Both horizontal on same Y
	if (sfy === sty && mfy === mty && sfy === mfy) {
		const sMin = Math.min(sfx, stx);
		const sMax = Math.max(sfx, stx);
		const mMin = Math.min(mfx, mtx);
		const mMax = Math.max(mfx, mtx);

		if (sMin < mMax && sMax > mMin) {
			return { overlaps: true, maskMin: mMin, maskMax: mMax, axis: 'x' };
		}
	}

	// Both vertical on same X
	if (sfx === stx && mfx === mtx && sfx === mfx) {
		const sMin = Math.min(sfy, sty);
		const sMax = Math.max(sfy, sty);
		const mMin = Math.min(mfy, mty);
		const mMax = Math.max(mfy, mty);

		if (sMin < mMax && sMax > mMin) {
			return { overlaps: true, maskMin: mMin, maskMax: mMax, axis: 'y' };
		}
	}

	return { overlaps: false, maskMin: 0, maskMax: 0, axis: null };
};

/**
 * Finds intersection points where mask vertices lie on a shape edge (T-junctions).
 * Returns the X or Y coordinates where the shape edge should be split.
 */
const findTJunctionSplitPoints = (
	shapeVector: Vector<Vertex2>,
	maskPolygon: Polygon
): Array<number> => {
	const sfx = nearestSixteenth(shapeVector.from.x);
	const sfy = nearestSixteenth(shapeVector.from.y);
	const stx = nearestSixteenth(shapeVector.to.x);
	const sty = nearestSixteenth(shapeVector.to.y);

	const splitPoints: Array<number> = [];
	const isHorizontal = sfy === sty;
	const isVertical = sfx === stx;

	if (!isHorizontal && !isVertical) return []; // Only handle axis-aligned edges

	// Get all unique vertices from the mask polygon
	const maskVertices = polygonToVertices(maskPolygon);

	for (const mv of maskVertices) {
		if (isHorizontal) {
			// Horizontal shape edge: check if mask vertex is on this line
			if (mv.y === sfy) {
				const sMin = Math.min(sfx, stx);
				const sMax = Math.max(sfx, stx);
				// Check if mask vertex X is strictly within the edge (not at endpoints)
				if (mv.x > sMin && mv.x < sMax) {
					splitPoints.push(mv.x);
				}
			}
		} else if (isVertical) {
			// Vertical shape edge: check if mask vertex is on this line
			if (mv.x === sfx) {
				const sMin = Math.min(sfy, sty);
				const sMax = Math.max(sfy, sty);
				// Check if mask vertex Y is strictly within the edge (not at endpoints)
				if (mv.y > sMin && mv.y < sMax) {
					splitPoints.push(mv.y);
				}
			}
		}
	}

	// Remove duplicates and sort
	return [...new Set(splitPoints)].sort((a, b) => a - b);
};

/**
 * Splits a shape edge at the given points, preserving direction
 */
const splitEdgeAtPoints = (
	shapeVector: Vector<Vertex2>,
	splitPoints: Array<number>
): Array<Vector<Vertex2>> => {
	if (splitPoints.length === 0) return [shapeVector];

	const sfx = nearestSixteenth(shapeVector.from.x);
	const sfy = nearestSixteenth(shapeVector.from.y);
	const stx = nearestSixteenth(shapeVector.to.x);
	const sty = nearestSixteenth(shapeVector.to.y);

	const isHorizontal = sfy === sty;
	const result: Array<Vector<Vertex2>> = [];

	if (isHorizontal) {
		// Horizontal edge - split on X
		const goesRight = sfx < stx;
		// Sort split points in the direction of travel
		const sortedSplits = [...splitPoints].sort((a, b) => (goesRight ? a - b : b - a));

		// Build segments from from -> splits -> to
		let currentX = sfx;
		for (const splitX of sortedSplits) {
			// Only add if split is between current and destination
			const isBetween = goesRight
				? (splitX > currentX && splitX < stx)
				: (splitX < currentX && splitX > stx);

			if (isBetween) {
				result.push({
					...shapeVector,
					from: new Vertex2({ dataIn: { x: currentX, y: sfy }}),
					to: new Vertex2({ dataIn: { x: splitX, y: sfy }})
				});
				currentX = splitX;
			}
		}

		// Add final segment
		if (currentX !== stx) {
			result.push({
				...shapeVector,
				from: new Vertex2({ dataIn: { x: currentX, y: sfy }}),
				to: new Vertex2({ dataIn: { x: stx, y: sty }})
			});
		}
	} else {
		// Vertical edge - split on Y
		const goesUp = sfy < sty;
		// Sort split points in the direction of travel
		const sortedSplits = [...splitPoints].sort((a, b) => (goesUp ? a - b : b - a));

		// Build segments from from -> splits -> to
		let currentY = sfy;
		for (const splitY of sortedSplits) {
			// Only add if split is between current and destination
			const isBetween = goesUp
				? (splitY > currentY && splitY < sty)
				: (splitY < currentY && splitY > sty);

			if (isBetween) {
				result.push({
					...shapeVector,
					from: new Vertex2({ dataIn: { x: sfx, y: currentY }}),
					to: new Vertex2({ dataIn: { x: sfx, y: splitY }})
				});
				currentY = splitY;
			}
		}

		// Add final segment
		if (currentY !== sty) {
			result.push({
				...shapeVector,
				from: { x: sfx, y: currentY } as Vertex2,
				to: { x: stx, y: sty } as Vertex2
			});
		}
	}

	return result.length > 0 ? result : [shapeVector];
};

/**
 * Cuts a shape edge where it overlaps with a mask edge.
 * Returns the remaining portions of the shape edge (outside the mask), preserving direction.
 */
const cutEdgeWithMask = (
	shapeVector: Vector<Vertex2>,
	maskFrom: Vertex2,
	maskTo: Vertex2
): Array<Vector<Vertex2>> => {
	const sfx = nearestSixteenth(shapeVector.from.x);
	const sfy = nearestSixteenth(shapeVector.from.y);
	const stx = nearestSixteenth(shapeVector.to.x);
	const sty = nearestSixteenth(shapeVector.to.y);

	const overlap = edgesOverlap(
		new Vertex2({ dataIn: { x: sfx, y: sfy }}),
		new Vertex2({ dataIn: { x: stx, y: sty }}),
		maskFrom,
		maskTo
	);

	if (!overlap.overlaps || !overlap.axis) {
		return [shapeVector];
	}

	const result: Array<Vector<Vertex2>> = [];

	if (overlap.axis === 'x') {
		// Horizontal edge - cut on X axis
		const sMin = Math.min(sfx, stx);
		const sMax = Math.max(sfx, stx);
		const y = sfy;
		const goesRight = sfx < stx;

		// Calculate the remaining portions (outside mask overlap)
		const leftStart = sMin;
		const leftEnd = Math.min(sMax, overlap.maskMin);
		const rightStart = Math.max(sMin, overlap.maskMax);
		const rightEnd = sMax;

		if (leftStart < leftEnd) {
			// Left portion exists
			result.push({
				...shapeVector,
				from: new Vertex2({ dataIn: { x: goesRight ? leftStart : leftEnd, y }}),
				to: new Vertex2({ dataIn: { x: goesRight ? leftEnd : leftStart, y }})
			});
		}

		if (rightStart < rightEnd) {
			// Right portion exists
			result.push({
				...shapeVector,
				from: new Vertex2({ dataIn: { x: goesRight ? rightStart : rightEnd, y }}),
				to: new Vertex2({ dataIn: { x: goesRight ? rightEnd : rightStart, y }})
			});
		}
	} else {
		// Vertical edge - cut on Y axis
		const sMin = Math.min(sfy, sty);
		const sMax = Math.max(sfy, sty);
		const x = sfx;
		const goesUp = sfy < sty;

		// Calculate the remaining portions (outside mask overlap)
		const bottomStart = sMin;
		const bottomEnd = Math.min(sMax, overlap.maskMin);
		const topStart = Math.max(sMin, overlap.maskMax);
		const topEnd = sMax;

		if (bottomStart < bottomEnd) {
			// Bottom portion exists
			result.push({
				...shapeVector,
				from: new Vertex2({ dataIn: { x, y: goesUp ? bottomStart : bottomEnd }}),
				to: new Vertex2({ dataIn: { x, y: goesUp ? bottomEnd : bottomStart }})
			});
		}

		if (topStart < topEnd) {
			// Top portion exists
			result.push({
				...shapeVector,
				from: new Vertex2({ dataIn: { x, y: goesUp ? topStart : topEnd }}),
				to: new Vertex2({ dataIn: { x, y: goesUp ? topEnd : topStart }})
			});
		}
	}

	return result;
};

/**
 * Applies a mask polygon to a shape polygon, creating a cutout.
 * Returns the resulting vectors after masking.
 *
 * Algorithm:
 * 1. Find T-junction points where mask vertices lie on shape edges
 * 2. Split shape edges at these T-junction points
 * 3. For each edge segment, check if midpoint is inside mask - remove if so
 * 4. Add mask edges that are inside the shape polygon
 */
const applyMaskToPolygon = (
	shapePolygon: Polygon,
	maskPolygon: Polygon,
	debug = false
): Array<Vector<Vertex2>> => {
	const result: Array<Vector<Vertex2>> = [];
	const shapeVertices = polygonToVertices(shapePolygon);
	const maskVertices = polygonToVertices(maskPolygon);

	if (debug) {
		console.log('applyMaskToPolygon - shapeVertices:', JSON.stringify(shapeVertices));
		console.log('applyMaskToPolygon - maskVertices:', JSON.stringify(maskVertices));
	}

	// Step 1 & 2: Split shape edges at T-junction points and collinear overlaps
	for (const shapeVector of shapePolygon) {
		// First find T-junction split points
		const tJunctionPoints = findTJunctionSplitPoints(shapeVector, maskPolygon);

		if (debug && tJunctionPoints.length > 0) {
			const sfx = nearestSixteenth(shapeVector.from.x);
			const sfy = nearestSixteenth(shapeVector.from.y);
			const stx = nearestSixteenth(shapeVector.to.x);
			const sty = nearestSixteenth(shapeVector.to.y);
			console.log(`T-junction splits on (${sfx},${sfy})->(${stx},${sty}):`, tJunctionPoints);
		}

		// Split the edge at T-junction points
		let currentEdges = splitEdgeAtPoints(shapeVector, tJunctionPoints);

		// Then apply collinear cuts for each edge segment
		for (const maskVector of maskPolygon) {
			const maskFrom = new Vertex2({ dataIn: { x: nearestSixteenth(maskVector.from.x), y: nearestSixteenth(maskVector.from.y) }});
			const maskTo = new Vertex2({ dataIn: { x: nearestSixteenth(maskVector.to.x), y: nearestSixteenth(maskVector.to.y) }});

			const newEdges: Array<Vector<Vertex2>> = [];
			for (const edge of currentEdges) {
				newEdges.push(...cutEdgeWithMask(edge, maskFrom, maskTo));
			}
			currentEdges = newEdges;
		}

		// Step 3: Filter out edge segments whose midpoint is inside the mask
		for (const edge of currentEdges) {
			const ex1 = nearestSixteenth(edge.from.x);
			const ey1 = nearestSixteenth(edge.from.y);
			const ex2 = nearestSixteenth(edge.to.x);
			const ey2 = nearestSixteenth(edge.to.y);

			const midpoint = new Vertex2({ dataIn: { x: (ex1 + ex2) / 2, y: (ey1 + ey2) / 2 }});
			const isInside = isPointInPolygon(midpoint, maskVertices);

			// Only keep edges whose midpoint is NOT inside the mask
			if (!isInside) {
				result.push(edge);
			}
		}
	}

	// Step 4: Add mask edges that are inside the shape
	// NOTE: Mask edges are REVERSED when adding them because for a boolean subtraction,
	// the mask boundary should be wound in the opposite direction from the shape.
	for (const maskVector of maskPolygon) {
		const maskFrom = new Vertex2({ dataIn: { x: nearestSixteenth(maskVector.from.x), y: nearestSixteenth(maskVector.from.y) }});
		const maskTo = new Vertex2({ dataIn: { x: nearestSixteenth(maskVector.to.x), y: nearestSixteenth(maskVector.to.y) }});

		const midpoint = new Vertex2({ dataIn: { x: (maskFrom.x + maskTo.x) / 2, y: (maskFrom.y + maskTo.y) / 2 }});

		// Check if this mask edge overlaps with any shape edge (on the boundary)
		let overlapsWithShape = false;
		for (const shapeVector of shapePolygon) {
			const shapeFrom = new Vertex2({ dataIn: { x: nearestSixteenth(shapeVector.from.x), y: nearestSixteenth(shapeVector.from.y) }});
			const shapeTo = new Vertex2({ dataIn: { x: nearestSixteenth(shapeVector.to.x), y: nearestSixteenth(shapeVector.to.y) }});

			const overlap = edgesOverlap(shapeFrom, shapeTo, maskFrom, maskTo);
			if (overlap.overlaps) {
				overlapsWithShape = true;
				break;
			}
		}

		const isInsideShape = isPointInPolygon(midpoint, shapeVertices);

		if (debug) {
			console.log(`Mask edge (${maskFrom.x},${maskFrom.y})->(${maskTo.x},${maskTo.y}) overlaps: ${overlapsWithShape}, inside shape: ${isInsideShape}`);
		}

		// If mask edge is inside the shape (not on boundary), add it REVERSED
		// Reversing is required because the mask defines the area to remove,
		// so its edges need to be wound opposite to the shape for proper loop formation
		if (!overlapsWithShape && isInsideShape) {
			const reversedEdge = {
				...maskVector,
				from: maskVector.to,
				to: maskVector.from
			};
			result.push(reversedEdge);
		}
	}

	return result;
};

/**
 * Groups vectors into closed polygons by tracing to→from connections.
 * Also checks for reverse matches (to→to) and flips the vector if needed.
 */
const groupVectorsIntoPolygons = (vectors: Array<Vector<Vertex2>>): Array<Polygon> => {
	const polygons: Array<Polygon> = [];
	const used = new Set<number>();

	for (let i = 0; i < vectors.length; i++) {
		if (used.has(i)) continue;

		const polygon: Polygon = [];
		let current = vectors[i];
		polygon.push(current);
		used.add(i);

		// Follow the chain: find vector whose from (or to, reversed) matches current's to
		let searching = true;
		while (searching) {
			searching = false;
			const targetX = nearestSixteenth(current.to.x);
			const targetY = nearestSixteenth(current.to.y);

			for (let j = 0; j < vectors.length; j++) {
				if (used.has(j)) continue;

				const fromX = nearestSixteenth(vectors[j].from.x);
				const fromY = nearestSixteenth(vectors[j].from.y);
				const toX = nearestSixteenth(vectors[j].to.x);
				const toY = nearestSixteenth(vectors[j].to.y);

				// Check forward match: vector's from matches our target
				if (fromX === targetX && fromY === targetY) {
					current = vectors[j];
					polygon.push(current);
					used.add(j);
					searching = true;
					break;
				}

				// Check reverse match: vector's to matches our target (use reversed)
				if (toX === targetX && toY === targetY) {
					// Create reversed vector
					current = {
						...vectors[j],
						from: vectors[j].to,
						to: vectors[j].from
					};
					polygon.push(current);
					used.add(j);
					searching = true;
					break;
				}
			}
		}

		polygons.push(polygon);
	}

	return polygons;
};

export type ApplyVectorMaskOptions = {
	shapeVectors: Array<Vector<Vertex2>>;
	maskVectors: Array<Vector<Vertex2>>;
};

/**
 * Applies mask vectors to shape vectors, creating a cutout.
 *
 * @param options.shapeVectors - The main shape vectors (closed loop)
 * @param options.maskVectors - The mask vectors (one or more closed loops to cut out)
 * @returns The resulting vectors with cutout applied
 */
export const applyVectorMask = ({ shapeVectors, maskVectors }: ApplyVectorMaskOptions): Array<Vector<Vertex2>> => {
	// Step 1: Clip mask vectors to input bounds
	const bounds = getBoundingBox(shapeVectors);
	const clippedMaskVectors: Array<Vector<Vertex2>> = [];

	for (const maskVector of maskVectors) {
		const clipped = clipVectorToBounds(maskVector, bounds);
		if (clipped) {
			clippedMaskVectors.push(clipped);
		}
	}

	// Step 2: Group vectors into polygons
	const shapePolygons = groupVectorsIntoPolygons(shapeVectors);
	const maskPolygons = groupVectorsIntoPolygons(clippedMaskVectors);

	console.log('applyVectorMask bounds:', bounds);
	console.log('applyVectorMask shapePolygons:', shapePolygons.length, 'with vectors:', shapePolygons.map((p) => p.length));
	console.log('applyVectorMask maskPolygons:', maskPolygons.length, 'with vectors:', maskPolygons.map((p) => p.length));

	// Step 3: Find overlapping polygon pairs
	const overlaps = findOverlappingPolygons(shapePolygons, maskPolygons);
	console.log('applyVectorMask overlaps:', Array.from(overlaps.entries()));

	// Step 4: Apply masks to shapes
	const resultVectors: Array<Vector<Vertex2>> = [];
	const enableDebug = maskPolygons.length > 1; // Debug when we have L-shaped masks (like C3C30)

	for (let si = 0; si < shapePolygons.length; si++) {
		const overlappingMaskIndices = overlaps.get(si) ?? [];

		if (overlappingMaskIndices.length === 0) {
			// No masks overlap this shape - keep original vectors
			resultVectors.push(...shapePolygons[si]);
		} else {
			// Apply each overlapping mask to the shape
			let currentVectors: Array<Vector<Vertex2>> = [...shapePolygons[si]];

			for (const mi of overlappingMaskIndices) {
				const maskPolygon = maskPolygons[mi];
				const newVectors: Array<Vector<Vertex2>> = [];

				// Regroup current vectors into a polygon for processing
				const currentPolygons = groupVectorsIntoPolygons(currentVectors);

				for (const currentPolygon of currentPolygons) {
					// Enable debug logging for first shape/mask pair with L-shaped mask
					const debug = enableDebug && si === 0 && mi === 0;
					newVectors.push(...applyMaskToPolygon(currentPolygon, maskPolygon, debug));
				}

				currentVectors = newVectors;
			}

			resultVectors.push(...currentVectors);
		}
	}

	const finalVectors = filterZeroLengthVectors(resultVectors);
	console.log('applyVectorMask resultVectors:', finalVectors.length);

	// Debug: show first polygon's edges for C3C30 mask
	if (maskPolygons.length > 1) {
		console.log('First polygon result edges:');
		const [firstPolygon] = groupVectorsIntoPolygons(finalVectors);
		if (firstPolygon) {
			for (const edge of firstPolygon) {
				const fx = nearestSixteenth(edge.from.x);
				const fy = nearestSixteenth(edge.from.y);
				const tx = nearestSixteenth(edge.to.x);
				const ty = nearestSixteenth(edge.to.y);
				console.log(`  (${fx},${fy})->(${tx},${ty})`);
			}
		}
	}

	return finalVectors;
};
